import * as React from 'react'
import { ReactNode } from 'react'
import { useLoaderData } from 'react-router'

export type CachedLoaderDataContextType = ReturnType<typeof useCachedLoaderData>

export const CachedLoaderDataContext = React.createContext<{
  data: any
  isCached: boolean
} | null>(null)

export function useCachedLoaderData<DataType>() {
  const context = React.useContext<{ data: DataType; isCached: boolean } | null>(CachedLoaderDataContext)

  if (context === null) {
    throw new Error(
      'Error! Extracting context without wrapping your component with the CachedLoaderDataContextProvider'
    )
  }

  return context
}

interface CachedLoaderDataContextProviderProps {
  children: ReactNode
  isNullValidData?: boolean
}
export function CachedLoaderDataContextProvider<DataType>({
  children,
  isNullValidData = false,
}: CachedLoaderDataContextProviderProps) {
  const loaderData = useLoaderData() as DataType
  const dataCacheRef = React.useRef<DataType>(loaderData)

  const value = React.useMemo(() => {
    if (loaderData !== undefined && (loaderData !== null || isNullValidData)) {
      dataCacheRef.current = loaderData
    }
    return { data: dataCacheRef.current, isCached: !loaderData }
  }, [loaderData, isNullValidData])

  return <CachedLoaderDataContext.Provider value={value}>{children}</CachedLoaderDataContext.Provider>
}
